import { useEffect, useState } from 'react'
import { Loader2 } from '@/lib/icons'
import { formatSom } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'

export interface CancelPayoutDialogProps {
  open: boolean
  bloggerName: string
  amount: number
  isSubmitting?: boolean
  errorMessage?: string | null
  onCancel: () => void
  onSubmit: (note: string) => void
}

/**
 * So'rovni bekor qilish — `docs/03-app-flow.md`: "sabab yoziladi, so'rov CANCELLED bo'ladi,
 * blogerga xabar boradi. Videolar tegilmaydi... balans ochiq qoladi." `PayoutCancelRequest.note`
 * kontraktda MAJBURIY (`packages/shared/src/api.ts`: "Blogerga sabab ko'rsatiladi") — shuning
 * uchun oddiy `ConfirmDialog` yetarli emas, `RejectReasonPicker` naqshiga mos alohida forma.
 */
export function CancelPayoutDialog({
  open,
  bloggerName,
  amount,
  isSubmitting = false,
  errorMessage,
  onCancel,
  onSubmit,
}: CancelPayoutDialogProps) {
  const [note, setNote] = useState('')

  useEffect(() => {
    if (open) setNote('')
  }, [open])

  const trimmed = note.trim()
  const isValid = trimmed.length > 0

  function handleSubmit() {
    if (!isValid) return
    onSubmit(trimmed)
  }

  return (
    <Modal
      open={open}
      onClose={onCancel}
      ariaLabel="So'rovni bekor qilish"
      size="compact"
      zIndex={50}
    >
      <div className="flex flex-col gap-4 p-5">
        <h2 className="text-base font-semibold">So&apos;rovni bekor qilaysizmi?</h2>
        <p className="text-sm text-app-hint">
          {bloggerName} — {formatSom(amount)}. Bloger o&apos;zi so&apos;ragan bu to&apos;lov bekor
          qilinadi va unga sabab bilan xabar boradi. Videolar va balans tegilmaydi — bloger keyinroq
          qayta so&apos;rov yubora oladi.
        </p>

        <div className="flex flex-col gap-1">
          <label htmlFor="cancel-note" className="text-xs font-medium text-app-hint">
            Sabab (majburiy, blogerga ko&apos;rsatiladi)
          </label>
          <textarea
            id="cancel-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
            placeholder="Masalan: karta ma'lumoti noto'g'ri, blogerga bog'lanildi"
            className="rounded-input border border-app-border bg-app-bg px-3 py-2 text-base text-app-text outline-none focus-visible:border-brand"
          />
        </div>

        {errorMessage && (
          <p className="text-xs font-medium text-stage-rejected-text">{errorMessage}</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            Yopish
          </Button>
          <Button variant="danger" onClick={handleSubmit} disabled={!isValid || isSubmitting}>
            {isSubmitting && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            Ha, bekor qilish
          </Button>
        </div>
      </div>
    </Modal>
  )
}
